import { useState, lazy, Suspense } from "react";
import { API } from "../../App";

const RichTextEditor = lazy(() => import("../RichTextEditor"));

export default function AdminEmailsTab({ adminKey }) {
  const [sendingReminders, setSendingReminders] = useState(false);
  const [sendingSummaries, setSendingSummaries] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [summaryResult, setSummaryResult] = useState(null);
  
  const [customEmailForm, setCustomEmailForm] = useState({
    subject: "",
    content: "",
    recipients: "all"
  });
  const [sendingCustomEmail, setSendingCustomEmail] = useState(false);
  const [customEmailResult, setCustomEmailResult] = useState(null);
  const [showCustomEmailModal, setShowCustomEmailModal] = useState(false);

  const sendWeeklySummaries = async () => {
    setSendingSummaries(true);
    setSummaryResult(null);
    try {
      const response = await fetch(`${API}/admin/send-weekly-summaries?admin_key=${adminKey}`, {
        method: 'POST'
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to send summaries');
      }
      setSummaryResult(result);
    } catch (err) {
      setSummaryResult({ error: err.message });
    } finally {
      setSendingSummaries(false);
    }
  };

  const sendCheckInReminders = async () => {
    setSendingReminders(true);
    setReminderResult(null);
    try {
      const response = await fetch(`${API}/admin/send-check-in-reminders?admin_key=${adminKey}`, {
        method: 'POST'
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to send reminders');
      }
      setReminderResult(result);
    } catch (err) {
      setReminderResult({ error: err.message });
    } finally {
      setSendingReminders(false);
    }
  };

  const sendTestEmail = async () => {
    try {
      const response = await fetch(`${API}/admin/send-custom-email?admin_key=${adminKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: customEmailForm.subject || "Test Email",
          content: customEmailForm.content,
          recipients: "test"
        })
      });
      const result = await response.json();
      if (response.ok) {
        alert(result.message);
      } else {
        alert(result.detail || "Failed to send test email");
      }
    } catch (err) {
      alert("Failed to send test email");
    }
  };

  const sendCustomEmail = async () => {
    if (!window.confirm(`Send this email to ${customEmailForm.recipients === 'all' ? 'ALL members' : customEmailForm.recipients === 'inner_circle' ? 'Inner Circle members' : 'Free members'}?`)) return;
    
    setSendingCustomEmail(true);
    try {
      const response = await fetch(`${API}/admin/send-custom-email?admin_key=${adminKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customEmailForm)
      });
      const result = await response.json();
      if (response.ok) {
        setCustomEmailResult(result);
        setShowCustomEmailModal(false);
      } else {
        alert(result.detail || "Failed to send email");
      }
    } catch (err) {
      alert("Failed to send email");
    } finally {
      setSendingCustomEmail(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-medium text-[#2D2A26] mb-6">Automated Email Triggers</h2>
      <p className="text-[#6E6A66] mb-8">
        Manually send batch emails to your users. These emails help re-engage users and provide value through emotional insights.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Weekly Emotional Summary */}
        <div className="bg-white rounded-xl border border-[#E8E4DE] p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-[#A87C6D]/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-[#A87C6D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-[#2D2A26]">Weekly Emotional Summary</h3>
              <p className="text-sm text-[#6E6A66] mt-1">
                Send personalized weekly emotional pattern summaries to Inner Circle members.
              </p>
            </div>
          </div>
          
          <div className="bg-[#F9F7F4] rounded-lg p-4 mb-4">
            <p className="text-sm text-[#6E6A66]">
              <strong>Recipients:</strong> Inner Circle members who have checked in this week
            </p>
            <p className="text-sm text-[#6E6A66] mt-1">
              <strong>Content:</strong> Mood trends, most-used prompts, and gentle insights
            </p>
          </div>

          {summaryResult && (
            <div className={`mb-4 p-3 rounded-lg ${summaryResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {summaryResult.error ? (
                <p className="text-sm">{summaryResult.error}</p>
              ) : (
                <p className="text-sm">
                  {summaryResult.message || `Sent ${summaryResult.sent} weekly summaries`}
                </p>
              )}
            </div>
          )}

          <button
            data-testid="send-weekly-summaries-btn"
            onClick={sendWeeklySummaries}
            disabled={sendingSummaries}
            className="w-full px-4 py-3 bg-[#A87C6D] text-white rounded-lg hover:bg-[#96695C] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {sendingSummaries ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Weekly Summaries
              </>
            )}
          </button>
        </div>

        {/* Check-in Reminders */}
        <div className="bg-white rounded-xl border border-[#E8E4DE] p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-[#7A9E7E]/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-[#7A9E7E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-[#2D2A26]">Gentle Check-in Reminders</h3>
              <p className="text-sm text-[#6E6A66] mt-1">
                Send warm, caring reminders to users who haven&apos;t checked in recently.
              </p>
            </div>
          </div>
          
          <div className="bg-[#F9F7F4] rounded-lg p-4 mb-4">
            <p className="text-sm text-[#6E6A66]">
              <strong>Recipients:</strong> Users inactive for 3, 7, or 11+ days
            </p>
            <p className="text-sm text-[#6E6A66] mt-1">
              <strong>Content:</strong> Personalized gentle nudge based on inactivity period
            </p>
          </div>

          {reminderResult && (
            <div className={`mb-4 p-3 rounded-lg ${reminderResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {reminderResult.error ? (
                <p className="text-sm">{reminderResult.error}</p>
              ) : (
                <p className="text-sm">
                  {reminderResult.message || `Sent ${reminderResult.total} reminders`}
                </p>
              )}
            </div>
          )}

          <button
            data-testid="send-check-in-reminders-btn"
            onClick={sendCheckInReminders}
            disabled={sendingReminders}
            className="w-full px-4 py-3 bg-[#7A9E7E] text-white rounded-lg hover:bg-[#6A8E6E] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {sendingReminders ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                Send Check-in Reminders
              </>
            )}
          </button>
        </div>
      </div>

      {/* Custom Email Section */}
      <div className="mt-8 pt-8 border-t border-[#E8E4DE]">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-[#2D2A26]">Custom Email Blast</h3>
            <p className="text-sm text-[#6E6A66] mt-1">Compose and send a custom email to your members</p>
          </div>
          <button
            onClick={() => {
              setCustomEmailForm({ subject: "", content: "", recipients: "all" });
              setCustomEmailResult(null);
              setShowCustomEmailModal(true);
            }}
            className="px-4 py-2 bg-[#A87C6D] text-white rounded-lg hover:bg-[#96695C] text-sm"
          >
            + Compose Email
          </button>
        </div>
        
        {customEmailResult && (
          <div className={`p-4 rounded-lg ${customEmailResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {customEmailResult.error ? (
              <p className="text-sm">{customEmailResult.error}</p>
            ) : (
              <p className="text-sm">{customEmailResult.message}</p>
            )}
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="mt-8 p-4 bg-[#F5F2EE] rounded-xl border border-[#E8E4DE]">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-[#6E6A66] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-[#6E6A66]">
            <p className="font-medium text-[#2D2A26] mb-1">About automated emails</p>
            <p>
              These emails are sent via Resend to encourage engagement and provide value. 
              Weekly summaries are exclusive to Inner Circle members, while check-in reminders 
              reach all users who haven&apos;t checked in recently.
            </p>
          </div>
        </div>
      </div>

      {/* Custom Email Modal */}
      {showCustomEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-[#2D2A26] mb-4">Compose Custom Email</h3>
            
            <div className="space-y-4">
              {/* Recipients */}
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-2">Recipients</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="recipients"
                      checked={customEmailForm.recipients === "all"}
                      onChange={() => setCustomEmailForm({...customEmailForm, recipients: "all"})}
                    />
                    <span className="text-sm">All Members</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="recipients"
                      checked={customEmailForm.recipients === "inner_circle"}
                      onChange={() => setCustomEmailForm({...customEmailForm, recipients: "inner_circle"})}
                    />
                    <span className="text-sm">Inner Circle Only</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="recipients"
                      checked={customEmailForm.recipients === "free"}
                      onChange={() => setCustomEmailForm({...customEmailForm, recipients: "free"})}
                    />
                    <span className="text-sm">Free Members Only</span>
                  </label>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-1">Subject Line</label>
                <input
                  type="text"
                  value={customEmailForm.subject}
                  onChange={(e) => setCustomEmailForm({...customEmailForm, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                  placeholder="Enter email subject..."
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-1">Email Content</label>
                <Suspense fallback={<div className="p-4 border border-[#E8E4DE] rounded-lg text-center text-[#6E6A66]">Loading editor...</div>}>
                  <RichTextEditor
                    value={customEmailForm.content}
                    onChange={(content) => setCustomEmailForm({...customEmailForm, content})}
                    placeholder="Write your email content here..."
                  />
                </Suspense>
              </div>
            </div>

            <div className="flex flex-col gap-3 mt-6">
              {/* Test Email Button */}
              <button
                onClick={sendTestEmail}
                disabled={!customEmailForm.subject || !customEmailForm.content}
                className="w-full px-4 py-2 border border-[#A87C6D] text-[#A87C6D] rounded-lg hover:bg-[#A87C6D]/10 disabled:opacity-50 text-sm"
              >
                📧 Send Test Email to Admin Only
              </button>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCustomEmailModal(false)}
                  className="flex-1 px-4 py-2 border border-[#E8E4DE] rounded-lg hover:bg-[#F5F2EE]"
                >
                  Cancel
                </button>
                <button
                  onClick={sendCustomEmail}
                  disabled={!customEmailForm.subject || !customEmailForm.content || sendingCustomEmail}
                  className="flex-1 px-4 py-2 bg-[#A87C6D] text-white rounded-lg hover:bg-[#96695C] disabled:opacity-50"
                >
                  {sendingCustomEmail ? "Sending..." : "Send to All Recipients"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
